import * as CL from '@design-system/component-library';
import * as React from 'react';
import { BREAKPOINT_S } from '@design-system/component-library/src/utils/css';
import {
  activationFeeWithVarMsg,
  activationFeesWithVarMsg,
  changeFeeMsg,
  saveMsg,
  t,
  totalMsg,
} from '../../common/i18n';
import { atContext, atType } from '../../common/constants/namingConventionVariables';
import { createProductStructuredDataFromOnlineModel } from '../../common/structured-data/product';
import { formatSumToString } from '../../common/utils/priceUtils';
import { isInBrowser } from '../../common/utils/ssrUtils';
import { isSubCardWithImpression } from '../SubscriptionCards/subscriptionCardsAnalyticsUtils';
import type { ItemList, WithContext } from 'schema-dts';
import type { Offer } from '../../generated/api/offer';
import type { SubscriptionCardWithModel } from './types';

export const isMobileView = isInBrowser() && window.innerWidth < BREAKPOINT_S;

export const getOffer = (offerCode?: string, offers?: Offer[]) => offers?.find(o => o.offerCode === offerCode);

export interface PriceProps {
  monthly?: number;
  onetime?: number;
  inModal?: boolean;
  quantity?: number;
  saving?: string;
  isChangeOffer?: boolean;
}

export const Price = ({ monthly, onetime, inModal, quantity = 1, saving, isChangeOffer }: PriceProps) => {
  const additionalInfo = `${quantity > 1 ? t.HWDV(activationFeesWithVarMsg, formatSumToString(onetime, true)) : t.HWDU(activationFeeWithVarMsg, formatSumToString(onetime, true))} €, Alv 0%`;
  const serviceFeeInfo = `${t.ZRKX(changeFeeMsg)} ${formatSumToString(onetime, true)} €, Alv 0%`;
  return (
    <CL.Price
      currentPrice={`${inModal ? `${t.CEQ2(totalMsg)}: ` : ''}${formatSumToString((monthly || 0) * quantity, true)}`} // * quantity
      currentPriceUnit="€/kk"
      additionalInfo={isChangeOffer ? serviceFeeInfo : additionalInfo}
      size={inModal ? 's' : undefined}
      saving={saving}
    />
  );
};

export const getSaving = (subscriptionCard?: SubscriptionCardWithModel, quantity: number = 1) => {
  if (subscriptionCard?.voucherCode && subscriptionCard.originalMonthlyPrice && subscriptionCard.monthlyPrice) {
    return `${t.HWDT(saveMsg)} ${formatSumToString((subscriptionCard.originalMonthlyPrice - subscriptionCard.monthlyPrice) * quantity, false)}`;
  }
  return undefined;
};

export interface CarouselWrapperProps {
  children: React.ReactNode;
}

export const MobileCarouselWrapper: React.FC<CarouselWrapperProps> = ({ children }) => {
  return isMobileView ? (
    <CL.Carousel buttons="primary" buttonLocation="top" snap="always">
      {children}
    </CL.Carousel>
  ) : (
    <>{children}</>
  );
};

export const getItemList = (
  subscriptions: SubscriptionCardWithModel[],
  siteBaseUrl: string,
  pathname: string
): WithContext<ItemList> => {
  // We need the onlineModel for the structured data.
  const items = subscriptions.filter(isSubCardWithImpression);
  return {
    [atContext]: 'https://schema.org',
    [atType]: 'ItemList',
    itemListElement: items
      .map((subData, idx) => {
        const structuredData = createProductStructuredDataFromOnlineModel(
          subData.onlineModel,
          siteBaseUrl,
          subData.offer.offerCode,
          subData.productName
        );
        return structuredData
          ? {
              ...structuredData,
              position: idx,
            }
          : undefined;
      })
      .filter(element => element != null),
    numberOfItems: items.length,
    url: `${siteBaseUrl}${pathname}`,
  };
};
